<template>
  <div class="space-y-4 bg-white border border-gray-200 rounded-lg p-6">
    <h2 class="text-lg font-semibold text-gray-900">Contract Assessment <font-awesome-icon :icon="['fas', 'wand-magic-sparkles']" class="text-blue-500 text-sm" /></h2>

    <template v-if="loaded && !contracts.length">
      <div class="bg-white border border-gray-200 rounded-lg px-6 py-12 text-center">
        <p class="text-sm text-gray-500">No contract assessments available for this tenant yet.</p>
      </div>
    </template>

    <template v-else-if="!canViewAssessment">
      <div class="bg-white border border-gray-200 rounded-lg px-6 py-12 text-center">
        <p class="text-sm text-gray-500">Contract Assessment is available to admin and manager_viewer users only.</p>
      </div>
    </template>

    <template v-else-if="contractId">
      <div class="flex flex-wrap items-center gap-3">
        <div class="inline-flex bg-white border border-gray-200 rounded-lg px-4 py-2 items-center gap-2 text-sm">
          <label class="font-medium text-gray-700">Contract</label>
          <select v-model="contractId" class="input-default text-sm">
            <option v-for="c in contracts" :key="c.contract_id" :value="c.contract_id">
              {{ demoLabel(c.contract_id, 'contract') }} ({{ c.practice_count }} practices)
            </option>
          </select>
        </div>

        <div class="inline-flex bg-white border border-gray-200 rounded-lg px-4 py-2 items-center gap-2 text-sm">
          <label class="font-medium text-gray-700">Metric</label>
          <select v-model="metricId" class="input-default text-sm">
            <option v-for="metric in metrics" :key="metric.id" :value="metric.id">
              {{ metric.id }} · {{ metric.label }}
            </option>
          </select>
        </div>

        <div class="inline-flex bg-white border border-gray-200 rounded-lg px-4 py-2 items-center gap-2 text-sm">
          <label class="font-medium text-gray-700">PTM</label>
          <select v-model="selectedPtm" class="input-default text-sm">
            <option value="">All</option>
            <option v-for="name in filterOptions.ptm_names" :key="name" :value="name">{{ name }}</option>
          </select>
        </div>

        <div class="inline-flex bg-white border border-gray-200 rounded-lg px-4 py-2 items-center gap-2 text-sm">
          <label class="font-medium text-gray-700">Pod</label>
          <select v-model="selectedPod" class="input-default text-sm">
            <option value="">All</option>
            <option v-for="name in filterOptions.pod_names" :key="name" :value="name">{{ name }}</option>
          </select>
        </div>

        <div class="inline-flex bg-white border border-gray-200 rounded-lg px-4 py-2 items-center gap-2 text-sm">
          <label class="font-medium text-gray-700">Region</label>
          <select v-model="selectedRegion" class="input-default text-sm">
            <option value="">All</option>
            <option v-for="name in filterOptions.region_names" :key="name" :value="name">{{ name }}</option>
          </select>
        </div>
      </div>

      <div class="bg-blue-50 border border-blue-200 rounded-lg px-4 py-3 text-xs text-blue-900">
        <span class="font-semibold">LLM-generated forensic assessment (GPT-5.4).</span>
        Rankings combine 2026 practice-level latest performance, 2026 longitudinal change, denominator size, and all available engagement transcript evidence. GPT-5.4 synthesized the findings and recommended actions practice-by-practice; these are qualitative judgments, not formulaic scores.
      </div>

      <div v-if="loading" class="py-12 text-center text-sm text-gray-400">Loading practice lists...</div>

      <template v-else>
        <div class="grid grid-cols-3 gap-3">
          <div class="bg-red-50 border border-red-200 rounded-lg px-4 py-3">
            <div class="text-[10px] uppercase tracking-wider text-red-600 font-semibold">Dragging Down</div>
            <div class="mt-1 text-2xl font-semibold text-gray-900">{{ totalBottomImpact }}</div>
            <div class="text-xs text-gray-500">size-adjusted completions below contract average</div>
          </div>
          <div class="bg-emerald-50 border border-emerald-200 rounded-lg px-4 py-3">
            <div class="text-[10px] uppercase tracking-wider text-emerald-700 font-semibold">Pushing Up</div>
            <div class="mt-1 text-2xl font-semibold text-gray-900">+{{ totalTopImpact }}</div>
            <div class="text-xs text-gray-500">size-adjusted completions above contract average</div>
          </div>
          <div class="bg-amber-50 border border-amber-200 rounded-lg px-4 py-3">
            <div class="text-[10px] uppercase tracking-wider text-amber-700 font-semibold">Blocked</div>
            <div class="mt-1 text-2xl font-semibold text-gray-900">{{ blockedCount }}</div>
            <div class="text-xs text-gray-500">practices where the bottleneck is structural, not just motivational</div>
          </div>
        </div>

        <div class="grid grid-cols-3 gap-4 items-start">
          <div v-for="group in orderedGroups" :key="group.list_type_id" class="border border-gray-200 rounded-lg overflow-hidden bg-white">
            <div class="px-4 py-3 border-b border-gray-200" :class="groupHeaderClass(group.group)">
              <div class="flex items-center justify-between gap-2">
                <div>
                  <div class="text-sm font-semibold text-gray-900">{{ groupLabel(group.group) }}</div>
                  <div class="text-[11px] text-gray-600 mt-0.5">{{ groupSubtitle(group.group) }}</div>
                </div>
                <div class="text-[11px] text-gray-500">{{ group.entries.length }} practices</div>
              </div>
            </div>

            <div v-if="!group.entries.length" class="px-4 py-6 text-xs text-gray-400">No practices match this filter.</div>

            <div v-else class="divide-y divide-gray-100">
              <div v-for="entry in group.entries" :key="entry.entity_id" class="px-4 py-3 hover:bg-gray-50 cursor-pointer" @click="toggleExpanded(entry)">
                <div class="flex items-start justify-between gap-3">
                  <div class="min-w-0">
                    <div class="flex items-center gap-2 min-w-0">
                      <span class="text-[10px] text-gray-400 font-mono w-5">{{ entry.rank_position }}</span>
                      <button class="sensitive text-sm font-medium text-blue-600 hover:underline truncate" data-spoof-kind="practice" @click.stop="drillToEntity(entry)">{{ entry.entity_name }}</button>
                    </div>
                    <div class="mt-1 text-[11px] text-gray-500 flex flex-wrap gap-x-3 gap-y-1">
                      <span v-if="entry.ptm_name">PTM: <span class="sensitive" data-spoof-kind="person">{{ entry.ptm_name }}</span></span>
                      <span v-if="entry.pod_name">Pod: <span class="sensitive" data-spoof-kind="person">{{ entry.pod_name }}</span></span>
                      <span v-if="entry.region_name">Region: {{ entry.region_name }}</span>
                    </div>
                  </div>
                  <div class="text-right shrink-0">
                    <div class="text-[10px] uppercase tracking-wider text-gray-400">Impact</div>
                    <div class="text-sm font-semibold" :class="impactClass(entry)">{{ formatImpact(entry.impact_vs_contract) }}</div>
                  </div>
                </div>

                <div class="mt-2 grid grid-cols-4 gap-2 text-[11px]">
                  <div>
                    <div class="text-gray-400 uppercase tracking-wider">Panel</div>
                    <div class="text-gray-700">{{ formatPanel(entry.panel_size) }}</div>
                  </div>
                  <div>
                    <div class="text-gray-400 uppercase tracking-wider">Latest</div>
                    <div class="text-gray-700">{{ pct(entry.latest_rate) }}</div>
                  </div>
                  <div>
                    <div class="text-gray-400 uppercase tracking-wider">2026 Δ</div>
                    <div :class="deltaClass(entry.delta_2026)">{{ pctSigned(entry.delta_2026) }}</div>
                  </div>
                  <div>
                    <div class="text-gray-400 uppercase tracking-wider">YoY Δ</div>
                    <div :class="deltaClass(entry.delta_yoy)">{{ pctSigned(entry.delta_yoy) }}</div>
                  </div>
                </div>

                <div class="mt-2 text-xs text-gray-700 leading-relaxed">{{ entry.headline }}</div>

                <div v-if="expandedId === entry.entity_id" class="mt-3 border border-gray-200 rounded-lg bg-gray-50 p-3 space-y-3 text-xs">
                  <div v-if="entry.summary">
                    <div class="font-semibold text-gray-900 uppercase tracking-wider text-[10px]">Assessment</div>
                    <div class="mt-1 text-gray-700 leading-relaxed">{{ entry.summary }}</div>
                  </div>

                  <div v-if="entry.blocker || entry.why_blocked">
                    <div class="font-semibold text-gray-900 uppercase tracking-wider text-[10px]">Block</div>
                    <div class="mt-1 text-amber-800 leading-relaxed">{{ entry.blocker || entry.why_blocked }}</div>
                  </div>

                  <div v-if="entry.why_it_matters">
                    <div class="font-semibold text-gray-900 uppercase tracking-wider text-[10px]">Why This Matters</div>
                    <div class="mt-1 text-gray-700 leading-relaxed">{{ entry.why_it_matters }}</div>
                  </div>

                  <div v-if="entry.evidence?.length">
                    <div class="font-semibold text-gray-900 uppercase tracking-wider text-[10px]">Evidence</div>
                    <ul class="mt-1 list-disc ml-4 space-y-1 text-gray-700">
                      <li v-for="(item, idx) in entry.evidence" :key="idx">{{ item }}</li>
                    </ul>
                  </div>

                  <div v-if="entry.actions?.length">
                    <div class="font-semibold text-gray-900 uppercase tracking-wider text-[10px]">Recommended Action</div>
                    <ul class="mt-1 list-disc ml-4 space-y-1 text-gray-700">
                      <li v-for="(item, idx) in entry.actions" :key="idx">{{ item }}</li>
                    </ul>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </template>
    </template>
  </div>
</template>

<script>
import axios from 'axios';
import { mapGetters } from 'vuex';
import { demoLabel } from '@/utils/demoSpoof';

const METRICS = [
  { id: 125, label: 'AWV+ YTD' },
  { id: 126, label: 'Chronic Condition Recapture (2Y)' },
  { id: 127, label: 'TOC IP Follow-Up 10 Days' },
]

export default {
  name: 'OverallPerformanceDashboard',
  props: {
    year: { type: Number, default: null },
  },
  data() {
    return {
      contractId: null,
      contracts: [],
      loaded: false,
      metricId: 125,
      filterOptions: {
        ptm_names: [],
        pod_names: [],
        region_names: [],
      },
      selectedPtm: '',
      selectedPod: '',
      selectedRegion: '',
      loading: false,
      groups: [],
      expandedId: null,
    };
  },
  computed: {
    ...mapGetters('auth', ['current_user']),
    ...mapGetters('tenant', ['current_tenant_slug', 'isTenantAdmin', 'isManagerViewer']),
    canViewAssessment() {
      return this.isTenantAdmin || this.isManagerViewer;
    },
    metrics() {
      return METRICS;
    },
    orderedGroups() {
      const order = ['bottom', 'top', 'blocked'];
      return [...this.groups].sort((a, b) => order.indexOf(a.group) - order.indexOf(b.group));
    },
    totalBottomImpact() {
      const bottom = this.groups.find(g => g.group === 'bottom');
      return Math.round(Math.abs((bottom?.entries || []).reduce((sum, entry) => sum + (entry.impact_vs_contract || 0), 0)));
    },
    totalTopImpact() {
      const top = this.groups.find(g => g.group === 'top');
      return Math.round((top?.entries || []).reduce((sum, entry) => sum + (entry.impact_vs_contract || 0), 0));
    },
    blockedCount() {
      const blocked = this.groups.find(g => g.group === 'blocked');
      return blocked?.entries?.length || 0;
    },
  },
  watch: {
    contractId() {
      this.resetFilters();
      this.fetchGroups();
    },
    metricId() {
      this.resetFilters();
      this.fetchGroups();
    },
    selectedPtm() { this.fetchGroups(); },
    selectedPod() { this.fetchGroups(); },
    selectedRegion() { this.fetchGroups(); },
  },
  mounted() {
    if (this.canViewAssessment) {
      this.fetchContracts();
    } else {
      this.loaded = true;
    }
  },
  methods: {
    demoLabel,
    async fetchContracts() {
      const response = await axios.get('/api/personal-analyst/practice-performance-lists/contracts/');
      this.contracts = response.data || [];
      if (this.contracts.length && !this.contractId) {
        this.contractId = this.contracts[0].contract_id;
      }
      this.loaded = true;
    },
    resetFilters() {
      this.selectedPtm = '';
      this.selectedPod = '';
      this.selectedRegion = '';
      this.filterOptions = { ptm_names: [], pod_names: [], region_names: [] };
      this.expandedId = null;
    },
    async fetchGroups() {
      if (!this.contractId || !this.metricId) return;
      this.loading = true;
      try {
        const params = {
          contract_id: this.contractId,
          metric_id: this.metricId,
        };
        if (this.selectedPtm) params.ptm_name = this.selectedPtm;
        if (this.selectedPod) params.pod_name = this.selectedPod;
        if (this.selectedRegion) params.region_name = this.selectedRegion;
        const { data } = await axios.get('/api/personal-analyst/practice-performance-lists/', { params });
        this.groups = data.groups || [];
        this.filterOptions = data.filters || { ptm_names: [], pod_names: [], region_names: [] };
      } catch (error) {
        console.error('Error fetching manual practice lists:', error);
        this.groups = [];
      } finally {
        this.loading = false;
      }
    },
    toggleExpanded(entry) {
      this.expandedId = this.expandedId === entry.entity_id ? null : entry.entity_id;
    },
    drillToEntity(entry) {
      if (entry.entity_id) {
        this.$router.push(`/entity/${entry.entity_id}/`);
      }
    },
    groupLabel(group) {
      if (group === 'top') return 'Practices Pushing Us Up';
      if (group === 'bottom') return 'Practices Weighing Us Down';
      if (group === 'blocked') return 'Practices That Are Blocked';
      return group;
    },
    groupSubtitle(group) {
      if (group === 'top') return 'Big helpers: size-adjusted performance plus evidence of real execution';
      if (group === 'bottom') return 'Big draggers: meaningful panel weight plus weak current performance';
      if (group === 'blocked') return 'Not just underperforming — structurally constrained or data-obscured';
      return '';
    },
    groupHeaderClass(group) {
      if (group === 'top') return 'bg-emerald-50';
      if (group === 'bottom') return 'bg-red-50';
      if (group === 'blocked') return 'bg-amber-50';
      return 'bg-gray-50';
    },
    impactClass(entry) {
      if ((entry.impact_vs_contract || 0) > 0) return 'text-emerald-600';
      if ((entry.impact_vs_contract || 0) < 0) return 'text-red-600';
      return 'text-gray-600';
    },
    deltaClass(value) {
      if (value === null || value === undefined) return 'text-gray-400';
      if (value > 0) return 'text-emerald-600';
      if (value < 0) return 'text-red-600';
      return 'text-gray-600';
    },
    formatImpact(value) {
      if (value === null || value === undefined) return '—';
      const rounded = Math.round(value);
      return `${rounded > 0 ? '+' : ''}${rounded}`;
    },
    formatPanel(value) {
      if (!value) return '—';
      return value >= 1000 ? `${(value / 1000).toFixed(1)}k` : `${value}`;
    },
    pct(value) {
      if (value === null || value === undefined) return '—';
      return `${(value * 100).toFixed(1)}%`;
    },
    pctSigned(value) {
      if (value === null || value === undefined) return '—';
      const formatted = `${(Math.abs(value) * 100).toFixed(1)}%`;
      if (value > 0) return `+${formatted}`;
      if (value < 0) return `-${formatted}`;
      return formatted;
    },
  },
};
</script>
